"use client";

import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useLayoutEffect,
  type ReactNode,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import { THEME_COOKIE, type ThemeMode } from "./theme-config";
import { createAppTheme } from "./mui-theme";

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error("useThemeMode debe usarse dentro de <ThemeModeProvider>");
  return ctx;
}

/**
 * Aplica el modo al instante: alterna la clase `.dark` en <html> (Tailwind +
 * tokens CSS) y persiste la cookie para que el SSR de la siguiente carga
 * renderice ya con el tema correcto (sin flash). No es dato sensible, por eso
 * la cookie no es httpOnly.
 */
function applyMode(mode: ThemeMode): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  // Desactiva transiciones/animaciones durante la conmutación para que el
  // cambio claro↔oscuro sea INSTANTÁNEO (sin "fundido" de colores). Tras
  // aplicar el cambio, se restaura todo en el siguiente frame.
  root.classList.add("theme-switching");
  root.classList.toggle("dark", mode === "dark");
  document.cookie = `${THEME_COOKIE}=${mode}; path=/; max-age=31536000; samesite=lax`;
  // Fuerza un reflow para que el navegador "fije" el nuevo tema sin transición…
  void window.getComputedStyle(root).backgroundColor;
  // …y restaura transiciones/animaciones en el siguiente frame.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      root.classList.remove("theme-switching");
    });
  });
}

/**
 * Provee el modo claro/oscuro y el `ThemeProvider` de MUI con el tema del modo
 * activo. `initial` lo calcula el servidor desde la cookie (ver layout), así el
 * primer render ya es correcto en cliente y servidor.
 */
export default function ThemeModeProvider({
  initial,
  children,
}: {
  initial: ThemeMode;
  children: ReactNode;
}) {
  const [mode, setModeState] = useState<ThemeMode>(initial);

  // El estado `mode` es la ÚNICA fuente de verdad. El efecto sobre el DOM (clase
  // `.dark` en <html> + cookie) se sincroniza SIEMPRE desde aquí, nunca dentro
  // del actualizador de estado: con el render concurrente de React 18 un
  // actualizador puede ejecutarse en un render que luego se descarta, dejando el
  // estado (y MUI) conmutado pero la clase `.dark` sin aplicar. `useLayoutEffect`
  // aplica el cambio antes del primer paint para que no haya flash de color.
  useLayoutEffect(() => {
    applyMode(mode);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const muiTheme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle, setMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
